import { present } from '@art-suite/art-core-ts';

const sanitizeCount = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return null;
  const floored = Math.floor(value);
  return floored < 0 ? 0 : floored;
};

export const getAllBathCounts = (bathroomCount?: number | null, halfBathCount?: number | null, fullBathCount?: number | null) => {
  let total = sanitizeCount(bathroomCount);
  let half = sanitizeCount(halfBathCount);
  let full = sanitizeCount(fullBathCount);
  if (total == null && (half != null || full != null)) total = (half ?? 0) + (full ?? 0);
  if (half == null && total != null && full != null) half = Math.max(total - full, 0);
  if (full == null && total != null && half != null) full = Math.max(total - half, 0);
  if (total != null && half == null) half = 0;
  if (total != null && full == null) full = Math.max(total - (half ?? 0), 0);
  if (total != null && half != null && half > total) half = total;
  if (total != null && full != null && full > Math.max(total - (half ?? 0), 0)) full = Math.max(total - (half ?? 0), 0);
  if (total != null && half != null && full != null && full + half !== total) total = full + half;
  return { bathroomCount: total ?? undefined, halfBathCount: half ?? undefined, fullBathCount: full ?? undefined };
};

export const getTotalBathCount = (bathroomCount?: number | null, halfBathCount?: number | null, fullBathCount?: number | null) => {
  const sanitized = getAllBathCounts(bathroomCount, halfBathCount, fullBathCount);
  if (sanitized.bathroomCount == null) return null;
  return sanitized.bathroomCount;
};

export const getFullBathCount = (bathroomCount?: number | null, halfBathCount?: number | null, fullBathCount?: number | null) => {
  const sanitized = getAllBathCounts(bathroomCount, halfBathCount, fullBathCount);
  if (sanitized.fullBathCount == null) return null;
  return sanitized.fullBathCount;
};

export const getHalfBathCount = (bathroomCount?: number | null, halfBathCount?: number | null, fullBathCount?: number | null) => {
  const sanitized = getAllBathCounts(bathroomCount, halfBathCount, fullBathCount);
  if (sanitized.halfBathCount == null) return null;
  return sanitized.halfBathCount;
};

/**
 * The rule is the output value is either bathroom count or if there are any half baths at all, it is bathroom count minus one half.
 * @param bathroomCount - The total number of bathrooms.
 * @param halfBathCount - The number of half bathrooms.
 * @param fullBathCount - The number of full bathrooms.
 * @returns The display value for the bathroom count.
 */
export const getBathroomDisplayValue = (bathroomCount?: number | null, halfBathCount?: number | null, fullBathCount?: number | null) => {
  const sanitized = getAllBathCounts(bathroomCount, halfBathCount, fullBathCount);
  if (sanitized.bathroomCount == null) return null;
  if (sanitized.fullBathCount == null) return sanitized.bathroomCount;
  if (sanitized.halfBathCount && sanitized.halfBathCount > 0) return sanitized.bathroomCount - 0.5;
  else return sanitized.bathroomCount;
};

export const formatBathroomDisplay = (bathroomCount?: number | null, halfBathCount?: number | null, placeholder = '-', fullBathCount?: number | null) => {
  const displayValue = getBathroomDisplayValue(bathroomCount, halfBathCount, fullBathCount);
  if (displayValue == null) return placeholder;
  return Number.isInteger(displayValue) ? String(displayValue) : displayValue.toFixed(1);
};

const getResolvedBathCounts = (
  fullBathCount: number | null | undefined,
  halfBathCount: number | null | undefined,
  totalBathCount: number | null | undefined,
): { fullBathCount: number; halfBathCount: number; totalBathCount: number } => {
  const rawFullBathCount = fullBathCount ?? 0;
  const rawHalfBathCount = halfBathCount ?? 0;
  const rawTotalBathCount = totalBathCount ?? rawFullBathCount + rawHalfBathCount;
  return {
    fullBathCount: fullBathCount ?? rawTotalBathCount - rawHalfBathCount,
    halfBathCount: halfBathCount ?? rawTotalBathCount - rawFullBathCount,
    totalBathCount: rawTotalBathCount,
  };
};

export const presentBathCount = (
  _fullBathCount: number | null | undefined,
  _halfBathCount: number | null | undefined,
  _totalBathCount: number | null | undefined,
) => {
  if (!present(_fullBathCount) && !present(_halfBathCount) && !present(_totalBathCount)) return '--';
  const { halfBathCount, totalBathCount } = getResolvedBathCounts(
    _fullBathCount,
    _halfBathCount,
    _totalBathCount,
  );
  if (halfBathCount > 0) return `${totalBathCount - 1}½`;
  return `${totalBathCount}`;
};
